import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { Button, Divider, Form } from 'antd';
import type { ValidatorRule } from 'rc-field-form/es/interface';
import { useState } from 'react';

import {
  SortableCollapse,
  SortablePanel,
} from 'app/components/layout/Sortable/Collapse';
import { createEmptySection } from 'app/components/pages/Edito/Event/Detail/EditoEventDetail';
import type { IEditoEventForm } from 'app/typings/edito';

import { EditoContainer } from '../../EditoContainer/EditoContainer';
import { EditoSectionsForm } from '../EditoSectionsForm/EditoSectionsForm';

import './EditoSectionsList.scss';

const MINIMUM_NB_SECTION = 1;
const MAXIMUM_NB_SECTION = 6;

type Props = {
  form: FormInstance<IEditoEventForm>;
};

const sectionsValidatorRule: ValidatorRule[] = [
  {
    validator: async (_, names) => {
      if (!names || names.length < MINIMUM_NB_SECTION) {
        return Promise.reject(
          new Error(`At least ${MINIMUM_NB_SECTION} section to publish`)
        );
      }

      if (!names || names.length > MAXIMUM_NB_SECTION) {
        return Promise.reject(
          new Error(`At most ${MAXIMUM_NB_SECTION} sections to publish`)
        );
      }
    },
  },
];

export const EditoSectionsList = ({ form }: Props) => {
  const sections = Form.useWatch('sections', form) || [];
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  return (
    <EditoContainer
      title="Sections"
      subTitle="Define the different sections users will see and interact with"
    >
      <Form.List name="sections" rules={sectionsValidatorRule}>
        {(fields, { add, remove, move }, { errors }) => {
          const itemIds = fields.map((field) => {
            const section = sections[field.name];

            return String(section?.id || section?.tempId || field.key);
          });

          return (
            <>
              <SortableCollapse
                activeKey={activeKeys}
                onToggle={setActiveKeys}
                items={itemIds}
                onSortEnd={({ oldIndex, newIndex }) => {
                  move(oldIndex, newIndex);
                }}
              >
                {fields.map((field, index) => {
                  const section = sections[field.name];
                  const title = section?.title || `Section ${index + 1}`;
                  const id = itemIds[index];

                  return (
                    <SortablePanel
                      key={id}
                      id={id}
                      className="edito-section-sortable"
                      title={title}
                      card
                      onRemove={() => remove(field.name)}
                    >
                      <div className="expanded-section">
                        <div className="expanded-header">
                          <span className="title">{`Section ${
                            index + 1
                          }`}</span>
                          <DeleteOutlined
                            className="deleteIcon"
                            onClick={() => remove(field.name)}
                          />
                        </div>
                        <EditoSectionsForm
                          fieldName={field.name}
                          form={form}
                          type="sections"
                        />
                      </div>
                    </SortablePanel>
                  );
                })}
              </SortableCollapse>

              <Form.ErrorList errors={errors} />
              <Divider />
              {fields.length < MAXIMUM_NB_SECTION && (
                <Button
                  className="addSectionButton"
                  type="primary"
                  ghost
                  icon={<PlusOutlined className="icon" />}
                  onClick={() => {
                    add({
                      ...createEmptySection(),
                      priority: fields.length + 1,
                    });
                    setActiveKeys([...activeKeys, `${fields.length}`]);
                  }}
                >
                  Add section
                </Button>
              )}
            </>
          );
        }}
      </Form.List>
    </EditoContainer>
  );
};
